import React from "react";
import {
  clampGeometry,
  DEFAULT_GEOMETRY,
  movedGeometry,
  PANEL_MARGIN,
  resizedGeometry,
  sanitizeGeometry,
  type PanelGeometry,
  type Viewport,
} from "./agentPanelGeometry.js";

export const PANEL_GEOMETRY_KEY = "msys-agent-panel-size";

/** A click (double-click to reset) is not a move: start after a few pixels. */
const DRAG_THRESHOLD = 4;

const currentViewport = (): Viewport => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

function readStored(): PanelGeometry {
  try {
    const raw = window.localStorage.getItem(PANEL_GEOMETRY_KEY);
    return raw ? sanitizeGeometry(JSON.parse(raw)) : DEFAULT_GEOMETRY;
  } catch {
    return DEFAULT_GEOMETRY;
  }
}

function persist(geometry: PanelGeometry) {
  try {
    window.localStorage.setItem(PANEL_GEOMETRY_KEY, JSON.stringify(geometry));
  } catch {
    // Storage blocked: the geometry just is not remembered.
  }
}

type Drag = {
  kind: "move" | "resize";
  pointerId: number;
  x: number;
  y: number;
  start: PanelGeometry;
  moving: boolean;
};

export type PanelPointerHandlers = {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  onLostPointerCapture: (event: React.PointerEvent<HTMLElement>) => void;
};

/**
 * The window's size and place, remembered per browser. The stored value is a
 * wish; what is rendered is always that wish clamped to the current viewport
 * (so a place from a larger screen, or after the browser window shrank, never
 * leaves the header out of reach). Dragging uses pointer capture on the
 * handle, so the gesture ends with the pointer even when it leaves the handle,
 * the window, or is cancelled by the browser (touch scrolling, gestures).
 */
export function useAgentPanelGeometry() {
  const [stored, setStored] = React.useState<PanelGeometry>(readStored);
  const [viewport, setViewport] = React.useState<Viewport>(currentViewport);
  const drag = React.useRef<Drag | null>(null);

  React.useEffect(() => {
    const onResize = () => setViewport(currentViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const geometry = React.useMemo(
    () => clampGeometry(stored, viewport),
    [stored, viewport]
  );
  const geometryRef = React.useRef(geometry);
  geometryRef.current = geometry;

  const begin =
    (kind: Drag["kind"]) => (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0 || drag.current) return;
      if (kind === "move" && geometryRef.current.expanded) return;
      if (kind === "resize") event.stopPropagation();
      event.preventDefault();
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch {
        // Capture is an enhancement; the gesture still works without it.
      }
      drag.current = {
        kind,
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        // Start from what is on screen, not from the stored wish.
        start: geometryRef.current,
        moving: kind === "resize",
      };
    };

  const place = (
    event: React.PointerEvent<HTMLElement>
  ): PanelGeometry | null => {
    const current = drag.current;
    if (!current || event.pointerId !== current.pointerId) return null;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (!current.moving && Math.hypot(dx, dy) < DRAG_THRESHOLD) return null;
    current.moving = true;
    const compute = current.kind === "move" ? movedGeometry : resizedGeometry;
    return compute(current.start, dx, dy, currentViewport());
  };

  const onMove = (event: React.PointerEvent<HTMLElement>) => {
    const next = place(event);
    if (next) setStored(next);
  };

  const finish = (event: React.PointerEvent<HTMLElement>, commit: boolean) => {
    const current = drag.current;
    if (!current || event.pointerId !== current.pointerId) return;
    const next = commit ? place(event) : null;
    drag.current = null;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      // Already released.
    }
    const final = next ?? (current.moving ? geometryRef.current : null);
    if (final) {
      setStored(final);
      persist(final);
    }
  };

  const handlers = (kind: Drag["kind"]): PanelPointerHandlers => ({
    onPointerDown: begin(kind),
    onPointerMove: onMove,
    onPointerUp: event => finish(event, true),
    onPointerCancel: event => finish(event, false),
    onLostPointerCapture: event => finish(event, false),
  });

  const update = (change: (current: PanelGeometry) => PanelGeometry) => {
    const next = clampGeometry(change(geometryRef.current), currentViewport());
    setStored(next);
    persist(next);
  };

  return {
    geometry,
    moveHandlers: handlers("move"),
    resizeHandlers: handlers("resize"),
    /** Back to the default corner (the size is kept). */
    resetPosition: () =>
      update(current => ({
        ...current,
        right: PANEL_MARGIN,
        bottom: PANEL_MARGIN,
      })),
    toggleExpanded: () =>
      update(current => ({ ...current, expanded: !current.expanded })),
  };
}
